import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

/**
 * MegaMenu Component - Modern multi-column dropdown menu
 * 
 * @param {Object} props
 * @param {string} props.label - Menu item label
 * @param {Array} props.sections - Array of menu sections with columns
 * @param {string} props.to - Optional link destination (if provided, label is clickable)
 * @param {boolean} props.hover - Use hover activation (default: true)
 * @param {string} props.className - Additional CSS classes
 */
const MegaMenu = ({ label, sections, to, hover = true, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          setIsOpen(false);
        }
      });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isOpen]);

  const handleMouseEnter = () => {
    if (hover) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setIsOpen(true);
    }
  };

  const handleMouseLeave = (e) => {
    if (hover) {
      // Check if we're moving to the dropdown menu or bridge
      const relatedTarget = e.relatedTarget;
      // Ensure relatedTarget is a valid Node before calling contains
      // Use try-catch as a safety net for edge cases
      try {
        if (menuRef.current && relatedTarget && relatedTarget instanceof Node && menuRef.current.contains(relatedTarget)) {
          return; // Don't close if moving to dropdown or bridge
        }
      } catch (error) {
        // If contains fails, just continue with the close logic
        console.warn('Error checking relatedTarget in handleMouseLeave:', error);
      }
      
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Check mouse position to detect upward movement
      const rect = menuRef.current?.getBoundingClientRect();
      if (rect) {
        const mouseY = e.clientY;
        const triggerBottom = rect.bottom;
        
        // If mouse is at or below the bottom of trigger, likely moving upward to menu
        // Use a larger threshold to catch upward movement
        if (mouseY >= triggerBottom - 15) {
          // Much longer delay for upward movement - gives plenty of time to reach bridge/dropdown
          timeoutRef.current = setTimeout(() => {
            // Only close if menu is actually closed (not being hovered)
            const dropdown = menuRef.current?.querySelector('.mega-menu-dropdown');
            const bridge = menuRef.current?.querySelector('.mega-menu-bridge');
            const isHoveringMenu = dropdown?.matches(':hover') || bridge?.matches(':hover');
            
            if (!isHoveringMenu && isOpen) {
              setIsOpen(false);
            }
            timeoutRef.current = null;
          }, 600); // Increased to 600ms for upward movement
        } else {
          // Shorter delay for downward/away movement
          timeoutRef.current = setTimeout(() => {
            setIsOpen(false);
            timeoutRef.current = null;
          }, 200);
        }
      } else {
        // Fallback delay - be generous
        timeoutRef.current = setTimeout(() => {
          setIsOpen(false);
          timeoutRef.current = null;
        }, 400);
      }
    }
  };

  const handleClick = (e) => {
    if (!hover) {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  const handleMenuMouseEnter = () => {
    if (hover) {
      // Clear any pending close timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setIsOpen(true); // Ensure menu stays open when hovering dropdown or bridge
    }
  };

  const handleMenuMouseLeave = (e) => {
    if (hover) {
      // Check if moving back to trigger
      const relatedTarget = e?.relatedTarget;
      const trigger = menuRef.current?.querySelector('.mega-menu-trigger');
      // Ensure relatedTarget is a valid Node before calling contains
      // Use try-catch as a safety net for edge cases
      try {
        if (trigger && relatedTarget && relatedTarget instanceof Node && trigger.contains(relatedTarget)) {
          return; // Don't close if moving back to trigger
        }
      } catch (error) {
        // If contains fails, just continue with the close logic
        console.warn('Error checking relatedTarget in handleMenuMouseLeave:', error);
      }
      
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        setIsOpen(false);
        timeoutRef.current = null;
      }, 200); // Small delay when leaving dropdown
    }
  };

  const LabelComponent = to ? Link : 'span';

  return (
    <div
      className={`mega-menu-wrapper ${className}`}
      ref={menuRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <LabelComponent
        to={to}
        className={`mega-menu-trigger ${isOpen ? 'active' : ''}`}
        onClick={handleClick}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {label}
        <span className="mega-menu-arrow">▼</span>
      </LabelComponent>

      {isOpen && (
        <>
          {/* Invisible bridge to catch upward mouse movement */}
          <div
            className="mega-menu-bridge"
            onMouseEnter={handleMenuMouseEnter}
            onMouseLeave={handleMenuMouseLeave}
          />
          <div
            className="mega-menu-dropdown"
            onMouseEnter={handleMenuMouseEnter}
            onMouseLeave={handleMenuMouseLeave}
            role="menu"
          >
          <div className="mega-menu-content">
            {sections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="mega-menu-section">
                {section.title && (
                  <h3 className="mega-menu-section-title">{section.title}</h3>
                )}
                <ul className="mega-menu-list">
                  {section.items.map((item, itemIndex) => {
                    const ItemComponent = item.to ? Link : 'div';
                    const isDisabled = !item.to && !item.onClick;
                    return (
                      <li key={itemIndex} className={`mega-menu-item ${isDisabled ? 'disabled' : ''}`}>
                        <ItemComponent
                          to={item.to}
                          className={`mega-menu-link ${isDisabled ? 'disabled-link' : ''}`}
                          onClick={(e) => {
                            if (isDisabled) {
                              e.preventDefault();
                              return;
                            }
                            if (item.onClick) {
                              item.onClick();
                            }
                            if (!hover) {
                              setIsOpen(false);
                            }
                          }}
                          style={isDisabled ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                        >
                          {item.icon && (
                            <span className="mega-menu-icon">{item.icon}</span>
                          )}
                          <div className="mega-menu-text">
                            <span className="mega-menu-label">{item.label}</span>
                            {item.description && (
                              <span className="mega-menu-description">
                                {item.description}
                              </span>
                            )}
                          </div>
                          {item.badge && (
                            <span className={`mega-menu-badge ${item.badge === 'TBD' ? 'badge-tbd' : ''}`}>
                              {item.badge}
                            </span>
                          )}
                        </ItemComponent>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
        </>
      )}
    </div>
  );
};

MegaMenu.propTypes = {
  label: PropTypes.string.isRequired,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          description: PropTypes.string,
          icon: PropTypes.node,
          to: PropTypes.string,
          onClick: PropTypes.func,
          badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
      ).isRequired,
    })
  ).isRequired,
  to: PropTypes.string,
  hover: PropTypes.bool,
  className: PropTypes.string,
};

export default MegaMenu;

